import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FaChevronRight, FaExclamationTriangle, FaSearch, FaTimes } from 'react-icons/fa';
import {
    ORDER_STATUSES, PARCEL_TONE, SOURCES, STATUS_TONE, fail, formatMoney, formatWhen, get, post,
} from './api';

const PAGE_SIZE = 25;

export const SourceBadge = ({ source }) => {
    const { t } = useTranslation();
    return <span className={`co-source co-source--${source}`}>{t(`commerceOrders.sources.${source}`, source)}</span>;
};

export const Chip = ({ tone = 'muted', children, title }) => (
    <span className={`co-chip co-chip--${tone}`} title={title}>{children}</span>
);

const SHOP_SOURCES = ['shopify', 'prestashop'];

// A parcel shipped for this order (tracking number). On a shop order whose
// shop writes tracking numbers, it is also written on the shop's order.
const AddTracking = ({ order, onAdded }) => {
    const { t } = useTranslation();
    const [form, setForm] = useState({ tracking_number: '', carrier: '', notify_customer: true });
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState(null);
    const shopOrder = SHOP_SOURCES.includes(order.source);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setMessage(null);
        try {
            const res = await post(`/orders/${order.id}/shipments`, {
                tracking_number: form.tracking_number.trim(), carrier: form.carrier.trim() || null,
                notify_customer: shopOrder ? form.notify_customer : false,
            });
            const push = res.data.shop_push;
            setMessage(push === 'queued'
                ? { tone: 'ok', text: t('commerceOrders.trackingQueued', 'Ajouté ; le numéro est en cours d’inscription sur la commande {{source}}.', { source: t(`commerceOrders.sources.${order.source}`, order.source) }) }
                : push === 'tracking_disabled'
                    ? { tone: 'wait', text: t('commerceOrders.trackingDisabled', 'Ajouté ici. L’inscription sur la commande de la boutique n’est pas activée (page Boutiques).') }
                    : { tone: 'ok', text: t('commerceOrders.trackingAdded', 'Colis ajouté.') });
            setForm({ tracking_number: '', carrier: '', notify_customer: form.notify_customer });
            onAdded(res.data.order);
        } catch (err) {
            setMessage({ tone: 'ko', text: fail(err, t('commerceOrders.trackingError', 'Le colis n’a pas pu être ajouté.')) });
        } finally {
            setBusy(false);
        }
    };

    return (
        <form className="co-add-tracking" onSubmit={submit}>
            <label className="co-field">
                <span>{t('commerceOrders.trackingNumber', 'Numéro de suivi')}</span>
                <input value={form.tracking_number} maxLength={64} onChange={(e) => setForm({ ...form, tracking_number: e.target.value })} />
            </label>
            <label className="co-field">
                <span>{t('commerceOrders.carrier', 'Transporteur')}</span>
                <input value={form.carrier} maxLength={64} onChange={(e) => setForm({ ...form, carrier: e.target.value })} placeholder="Colissimo" />
            </label>
            <button type="submit" className="u-btn u-btn--secondary" disabled={busy || form.tracking_number.trim().length < 4}>
                {t('commerceOrders.addTracking', 'Ajouter le suivi')}
            </button>
            {shopOrder && (
                <label className="co-check">
                    <input type="checkbox" checked={form.notify_customer} onChange={(e) => setForm({ ...form, notify_customer: e.target.checked })} />
                    <span>{t('commerceOrders.notifyCustomer', 'La boutique prévient son client (e-mail d’expédition)')}</span>
                </label>
            )}
            {message && <p className={`co-push co-push--${message.tone}`} role="status">{message.text}</p>}
        </form>
    );
};

const ShopPush = ({ push, source }) => {
    const { t } = useTranslation();
    if (!push) return null;
    const shop = t(`commerceOrders.sources.${source}`, source);
    if (push.status === 'done') return <p className="co-push co-push--ok">{t('commerceOrders.pushDone', 'Inscrit sur la commande {{shop}}', { shop })}</p>;
    if (push.status === 'failed' || push.status === 'retrying') {
        return <p className="co-push co-push--ko" title={push.error || ''}>
            {push.status === 'failed'
                ? t('commerceOrders.pushFailed', 'Non inscrit sur la commande {{shop}} : {{error}}', { shop, error: push.error || '—' })
                : t('commerceOrders.pushRetrying', 'Nouvel essai d’inscription sur {{shop}} : {{error}}', { shop, error: push.error || '—' })}
        </p>;
    }
    if (push.status === 'skipped') return <p className="co-push co-push--wait">{t('commerceOrders.pushSkipped', 'Non inscrit sur la boutique (usage désactivé).')}</p>;
    return <p className="co-push co-push--wait">{t('commerceOrders.pushQueued', 'Inscription sur la commande {{shop}} en cours…', { shop })}</p>;
};

const OrderDrawer = ({ orderId, onClose, onChanged }) => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language || 'fr';
    const [order, setOrder] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setOrder(null);
        get(`/orders/${orderId}`)
            .then((res) => { if (!cancelled) setOrder(res.data); })
            .catch((err) => { if (!cancelled) setError(fail(err, t('commerceOrders.detailError', 'Impossible de charger la commande.'))); });
        return () => { cancelled = true; };
    }, [orderId, t]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const clearProblem = async (trackingNumber) => {
        setBusy(true);
        setError('');
        try {
            const res = await post(`/orders/${orderId}/problems/clear`, { tracking_number: trackingNumber || null });
            setOrder(res.data);
            onChanged();
        } catch (err) {
            setError(fail(err, t('commerceOrders.clearError', 'Impossible de marquer le problème comme traité.')));
        } finally {
            setBusy(false);
        }
    };

    const customer = order?.customer || {};
    const address = order?.shipping_address || {};
    const openProblems = (order?.problems || []).filter((p) => !p.cleared_at);

    return (
        <div className="co-drawer-backdrop" onMouseDown={onClose}>
            <aside className="co-drawer" role="dialog" aria-modal="true" aria-label={t('commerceOrders.detailTitle', 'Commande')}
                onMouseDown={(e) => e.stopPropagation()}>
                <header className="co-drawer-header">
                    <div>
                        <span className="co-kicker">{t('commerceOrders.detailTitle', 'Commande')}</span>
                        <h2>{order?.number || '…'}</h2>
                        {order && (
                            <div className="co-drawer-meta">
                                <SourceBadge source={order.source} />
                                <Chip tone={STATUS_TONE[order.status]}>{t(`commerceOrders.statuses.${order.status}`, order.status)}</Chip>
                                {order.source_status && <span className="co-muted">{order.source_status}</span>}
                            </div>
                        )}
                    </div>
                    <button type="button" className="co-icon-btn" onClick={onClose} aria-label={t('commerceOrders.close', 'Fermer')}>
                        <FaTimes />
                    </button>
                </header>

                {error && <div className="co-alert co-alert--error" role="alert">{error}</div>}
                {!order && !error && <div className="co-loading">{t('commerceOrders.loading', 'Chargement…')}</div>}

                {order && (
                    <div className="co-drawer-body">
                        {openProblems.length > 0 && (
                            <section className="co-problem-panel">
                                <h3><FaExclamationTriangle aria-hidden="true" /> {t('commerceOrders.problemsTitle', 'Problèmes colis en cours')}</h3>
                                {openProblems.map((p) => (
                                    <div key={`${p.tracking_number}-${p.kind}`} className="co-problem">
                                        <div>
                                            <strong>{t(`commerceOrders.problems.${p.kind}`, p.kind)}</strong>
                                            <span className="co-muted">
                                                {p.tracking_number}{p.carrier ? ` · ${p.carrier}` : ''} · {formatWhen(p.detected_at, lang)}
                                            </span>
                                            {p.detail && <span className="co-problem-detail">« {p.detail} »</span>}
                                            {p.note && (
                                                <span className={`co-note co-note--${p.note.status}`}>
                                                    {p.note.status === 'written'
                                                        ? t('commerceOrders.noteWritten', 'Note ajoutée sur la commande {{source}}', { source: t(`commerceOrders.sources.${order.source}`, order.source) })
                                                        : t('commerceOrders.noteError', 'Note non écrite : {{error}}', { error: p.note.error || '—' })}
                                                </span>
                                            )}
                                        </div>
                                        <button type="button" className="u-btn u-btn--secondary" disabled={busy}
                                            onClick={() => clearProblem(p.tracking_number)}>
                                            {t('commerceOrders.markHandled', 'Marquer comme traité')}
                                        </button>
                                    </div>
                                ))}
                            </section>
                        )}

                        <section className="co-section">
                            <h3>{t('commerceOrders.parcelsTitle', 'Colis')}</h3>
                            {(order.shipments || []).length === 0 ? (
                                <p className="co-muted">{t('commerceOrders.noShipment', 'Aucun numéro de suivi sur cette commande pour l’instant.')}</p>
                            ) : (
                                <ul className="co-parcels">
                                    {order.shipments.map((s) => {
                                        const m = s.monitoring;
                                        return (
                                            <li key={s.tracking_number}>
                                                <div className="co-parcel-head">
                                                    <span className="co-mono">{s.tracking_number}</span>
                                                    {s.carrier && (
                                                        <span className="co-muted" title={s.carrier}>
                                                            {s.carrier_ref?.service_label || s.carrier}
                                                        </span>
                                                    )}
                                                    {m ? (
                                                        <Chip tone={m.flagged && m.alert_state !== 'resolved' ? 'danger' : PARCEL_TONE[m.status]}>
                                                            {t(`commerceOrders.parcelStatuses.${m.status}`, m.status)}
                                                        </Chip>
                                                    ) : (
                                                        <Chip tone="muted" title={t('commerceOrders.notMonitoredHint', 'Activez la surveillance colis pour suivre ce colis.')}>
                                                            {t('commerceOrders.notMonitored', 'Non surveillé')}
                                                        </Chip>
                                                    )}
                                                </div>
                                                {m?.last_event && (
                                                    <p className="co-muted co-parcel-event">{m.last_event} · {formatWhen(m.last_event_at, lang)}</p>
                                                )}
                                                <ShopPush push={s.shop_push} source={order.source} />
                                                {m && (
                                                    <Link className="co-link" to="/parcel-monitor">{t('commerceOrders.openMonitor', 'Voir dans la surveillance colis')}</Link>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                            <AddTracking order={order} onAdded={(next) => { setOrder(next); onChanged(); }} />
                        </section>

                        <section className="co-section co-grid-2">
                            <div>
                                <h3>{t('commerceOrders.customerTitle', 'Client')}</h3>
                                <dl className="co-dl">
                                    {order.client_ref && <><dt>{t('commerceOrders.clientRef', 'Client (B2B)')}</dt><dd>{order.client_ref}</dd></>}
                                    <dt>{t('commerceOrders.name', 'Nom')}</dt><dd>{customer.name || '—'}</dd>
                                    <dt>{t('commerceOrders.email', 'E-mail')}</dt><dd>{customer.email || '—'}</dd>
                                    <dt>{t('commerceOrders.phone', 'Téléphone')}</dt><dd>{customer.phone || '—'}</dd>
                                </dl>
                            </div>
                            <div>
                                <h3>{t('commerceOrders.addressTitle', 'Livraison')}</h3>
                                <p className="co-address">
                                    {[address.name, address.company, address.street, [address.postcode, address.city].filter(Boolean).join(' '), address.country]
                                        .filter(Boolean).map((line) => <span key={line}>{line}</span>)}
                                    {!address.street && !address.city && <span className="co-muted">—</span>}
                                </p>
                            </div>
                        </section>

                        <section className="co-section">
                            <h3>{t('commerceOrders.itemsTitle', 'Articles')}</h3>
                            {(order.items || []).length === 0 ? <p className="co-muted">—</p> : (
                                <table className="co-items">
                                    <tbody>
                                        {order.items.map((item, i) => (
                                            <tr key={`${item.sku}-${i}`}>
                                                <td>{item.name || item.sku || '—'}{item.sku && item.name && <span className="co-muted"> · {item.sku}</span>}</td>
                                                <td className="co-num">× {item.quantity}</td>
                                                <td className="co-num">{formatMoney(item.unit_price, order.currency, lang)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                            <p className="co-total">{t('commerceOrders.total', 'Total')} <strong>{formatMoney(order.total, order.currency, lang)}</strong></p>
                        </section>

                        <section className="co-section co-muted co-small">
                            {t('commerceOrders.placedAt', 'Passée le')} {formatWhen(order.placed_at, lang)} · {t('commerceOrders.updatedAt', 'Mise à jour')} {formatWhen(order.updated_at, lang)} · {t('commerceOrders.externalId', 'ID source')} <span className="co-mono">{order.external_id}</span>
                        </section>
                    </div>
                )}
            </aside>
        </div>
    );
};

const OrdersTab = ({ onGoTo, refreshKey, onChanged, initialQuery = '' }) => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language || 'fr';
    // ?q= (e.g. from a parcel's order reference in the parcel monitor) opens the search.
    const [filters, setFilters] = useState({ q: initialQuery, source: '', status: '', problem: false });
    const [query, setQuery] = useState(initialQuery);
    const [page, setPage] = useState(1);
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [openId, setOpenId] = useState(null);

    const load = useCallback(async () => {
        setError('');
        try {
            const params = { page, size: PAGE_SIZE };
            if (filters.q) params.q = filters.q;
            if (filters.source) params.source = filters.source;
            if (filters.status) params.status = filters.status;
            if (filters.problem) params.problem = true;
            const res = await get('/orders', params);
            setData(res.data);
        } catch (err) {
            setError(fail(err, t('commerceOrders.listError', 'Impossible de charger les commandes.')));
            setData({ items: [], total: 0 });
        }
    }, [filters, page, t]);

    useEffect(() => { load(); }, [load, refreshKey]);

    const setFilter = (patch) => { setFilters((f) => ({ ...f, ...patch })); setPage(1); };
    const items = data?.items || [];
    const totalPages = data ? Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE)) : 1;
    const hasFilters = Boolean(filters.q || filters.source || filters.status || filters.problem);

    return (
        <div className="co-card">
            <form className="co-toolbar" onSubmit={(e) => { e.preventDefault(); setFilter({ q: query.trim() }); }}>
                <label className="co-field co-field--grow">
                    <span>{t('commerceOrders.search', 'Recherche exacte')}</span>
                    <div className="co-search">
                        <input type="text" value={query} onChange={(e) => setQuery(e.target.value)}
                            placeholder={t('commerceOrders.searchPlaceholder', 'N° de commande, n° de suivi ou e-mail')} />
                        <button type="submit" className="co-icon-btn" aria-label={t('commerceOrders.searchButton', 'Rechercher')}><FaSearch /></button>
                    </div>
                </label>
                <label className="co-field">
                    <span>{t('commerceOrders.source', 'Source')}</span>
                    <select value={filters.source} onChange={(e) => setFilter({ source: e.target.value })}>
                        <option value="">{t('commerceOrders.allSources', 'Toutes')}</option>
                        {SOURCES.map((s) => <option key={s} value={s}>{t(`commerceOrders.sources.${s}`, s)}</option>)}
                    </select>
                </label>
                <label className="co-field">
                    <span>{t('commerceOrders.status', 'Statut')}</span>
                    <select value={filters.status} onChange={(e) => setFilter({ status: e.target.value })}>
                        <option value="">{t('commerceOrders.allStatuses', 'Tous')}</option>
                        {ORDER_STATUSES.map((s) => <option key={s} value={s}>{t(`commerceOrders.statuses.${s}`, s)}</option>)}
                    </select>
                </label>
                <label className="co-check">
                    <input type="checkbox" checked={filters.problem} onChange={(e) => setFilter({ problem: e.target.checked })} />
                    {t('commerceOrders.onlyProblems', 'Problèmes colis uniquement')}
                </label>
            </form>

            {error && <div className="co-alert co-alert--error" role="alert">{error}</div>}

            {data === null ? (
                <div className="co-skeleton" aria-hidden="true">{Array.from({ length: 6 }, (_, i) => <div key={i} />)}</div>
            ) : items.length === 0 ? (
                <div className="co-empty">
                    {hasFilters ? (
                        <p>{t('commerceOrders.emptyFiltered', 'Aucune commande ne correspond à cette recherche.')}</p>
                    ) : (
                        <>
                            <p className="co-empty-title">{t('commerceOrders.emptyTitle', 'Aucune commande pour l’instant')}</p>
                            <p>{t('commerceOrders.emptyHint', 'Connectez une boutique Shopify ou PrestaShop, ou envoyez vos commandes par l’API : leurs colis sont surveillés et vous êtes prévenu au moindre problème.')}</p>
                            <div className="co-empty-actions">
                                <button type="button" className="u-btn u-btn--primary" onClick={() => onGoTo('connections')}>
                                    {t('commerceOrders.connectShop', 'Connecter une boutique')}
                                </button>
                                <button type="button" className="u-btn u-btn--secondary" onClick={() => onGoTo('api')}>
                                    {t('commerceOrders.useApi', 'Utiliser l’API')}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            ) : (
                <div className="co-table-scroll">
                    <table className="co-table">
                        <thead>
                            <tr>
                                <th>{t('commerceOrders.colOrder', 'Commande')}</th>
                                <th>{t('commerceOrders.colCustomer', 'Client')}</th>
                                <th>{t('commerceOrders.colStatus', 'Statut')}</th>
                                <th>{t('commerceOrders.colParcels', 'Colis')}</th>
                                <th>{t('commerceOrders.colProblem', 'Problème')}</th>
                                <th className="co-num">{t('commerceOrders.colTotal', 'Total')}</th>
                                <th>{t('commerceOrders.colUpdated', 'Mise à jour')}</th>
                                <th aria-hidden="true" />
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((o) => {
                                const open = (o.problems || []).filter((p) => !p.cleared_at);
                                const first = o.shipments?.[0];
                                return (
                                    <tr key={o.id} className="co-row" tabIndex={0} onClick={() => setOpenId(o.id)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') setOpenId(o.id); }}>
                                        <td>
                                            <span className="co-cell-stack">
                                                <strong>{o.number}</strong>
                                                <SourceBadge source={o.source} />
                                            </span>
                                        </td>
                                        <td title={o.customer?.name || ''}>
                                            <span className="co-cell-stack">
                                                <span>{o.customer?.name || '—'}</span>
                                                {o.client_ref && <span className="co-muted">{o.client_ref}</span>}
                                            </span>
                                        </td>
                                        <td><Chip tone={STATUS_TONE[o.status]}>{t(`commerceOrders.statuses.${o.status}`, o.status)}</Chip></td>
                                        <td>
                                            {first ? (
                                                <span className="co-cell-stack">
                                                    <span className="co-mono">{first.tracking_number}</span>
                                                    <span className="co-muted">
                                                        {first.monitoring
                                                            ? t(`commerceOrders.parcelStatuses.${first.monitoring.status}`, first.monitoring.status)
                                                            : t('commerceOrders.notMonitored', 'Non surveillé')}
                                                        {o.shipments.length > 1 ? ` · +${o.shipments.length - 1}` : ''}
                                                    </span>
                                                </span>
                                            ) : <span className="co-muted">—</span>}
                                        </td>
                                        <td>
                                            {open.length > 0 ? (
                                                <Chip tone="danger">{t(`commerceOrders.problems.${open[0].kind}`, open[0].kind)}{open.length > 1 ? ` +${open.length - 1}` : ''}</Chip>
                                            ) : <span className="co-muted">—</span>}
                                        </td>
                                        <td className="co-num">{formatMoney(o.total, o.currency, lang)}</td>
                                        <td>{formatWhen(o.updated_at, lang)}</td>
                                        <td className="co-chevron"><FaChevronRight aria-hidden="true" /></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {data && totalPages > 1 && (
                <div className="co-card-footer">
                    <span className="co-muted">{t('commerceOrders.count', '{{total}} commandes', { total: (data.total || 0).toLocaleString(lang) })}</span>
                    <div className="co-pager">
                        <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)} aria-label={t('common.previous', 'Précédent')}>‹</button>
                        <span>{page} / {totalPages}</span>
                        <button type="button" disabled={page >= totalPages} onClick={() => setPage(page + 1)} aria-label={t('common.next', 'Suivant')}>›</button>
                    </div>
                </div>
            )}

            {openId && <OrderDrawer orderId={openId} onClose={() => setOpenId(null)} onChanged={() => { load(); onChanged(); }} />}
        </div>
    );
};

export default OrdersTab;
